import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import useSWRSubscription from "swr/subscription";
import { FIRESTORE_CHAT_SESSIONS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";

export function useChatSessions() {
  const { data, error } = useSWRSubscription("chatSessions", (key, { next }) => {
    const q = query(
      collection(firestore, `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_CHAT_SESSIONS_TABLE}`),
      orderBy("updatedAt", "desc"),
    );

    const sessionsData = new Map();

    const updateSessionsData = () => {
      const allSessions = Array.from(sessionsData.values());
      next(null, allSessions);
    };

    // Listen to sessions
    const unsubscribeSessions = onSnapshot(
      q,
      async (snapshot) => {
        try {
          snapshot.docChanges().forEach((change) => {
            const sessionDoc = change.doc;
            const sessionData = sessionDoc.data();
            const sessionId = sessionDoc.id;

            if (change.type === "added" || change.type === "modified") {
              // Update session data
              sessionsData.set(sessionId, {
                ...sessionData,
                id: sessionId,
              });
            } else if (change.type === "removed") {
              // Clean up when session is removed
              sessionsData.delete(sessionId);
            }
          });

          updateSessionsData();
        } catch (err) {
          next(err, null);
        }
      },
      (error) => {
        next(error, null);
      },
    );

    // Return cleanup function
    return () => {
      unsubscribeSessions();
    };
  });

  const updateChatSession = async (id, data) => {
    await updateDoc(
      doc(firestore, `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_CHAT_SESSIONS_TABLE}/${id}`),
      data,
    );
  };

  const saveOutgoingMessage = async (id, data, clientMessageId) => {
    const messagesCollection = collection(
      firestore,
      `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_CHAT_SESSIONS_TABLE}/${id}/messages`,
    );
    // Reserve the doc ID client-side (instead of addDoc) so a duplicate send attempt
    // (e.g. a double click before the first write round-trips) overwrites the same
    // document rather than creating a second message.
    const messageDocRef = clientMessageId ? doc(messagesCollection, clientMessageId) : doc(messagesCollection);
    await setDoc(messageDocRef, data);

    // Update lastMessage on chat session for performance
    await updateDoc(
      doc(firestore, `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_CHAT_SESSIONS_TABLE}/${id}`),
      {
        lastMessage: {
          id: messageDocRef.id,
          messageBody: data.messageBody || "",
          messageType: data.messageType,
          type: data.type,
          createdAt: data.createdAt,
          senderId: data.senderId,
          fileDownloadUrl: data.fileDownloadUrl || null,
          fileName: data.fileName || null,
        },
      },
    );

    return messageDocRef.id;
  };

  const enqueueOutgoingWhatsappMessage = async ({ chatId, messageId, message, scheduledAt }) => {
    const now = Math.floor(Date.now() / 1000);
    const payload = {
      chatId,
      messageId,
      message,
      status: "pending",
      retryCount: 0,
      createdAt: now,
      updatedAt: now,
      log: [],
    };

    if (scheduledAt) {
      payload.scheduledAt = scheduledAt;
    }

    const docRef = await addDoc(
      collection(firestore, `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/outgoingWhatsappMessagesQueue`),
      payload,
    );

    return docRef.id;
  };

  const updateChatMessage = async (chatId, messageId, data) => {
    await updateDoc(
      doc(
        firestore,
        `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_CHAT_SESSIONS_TABLE}/${chatId}/messages/${messageId}`,
      ),
      data,
    );
  };

  const cancelOutgoingWhatsappMessage = async ({ chatId, messageId, queueDocId }) => {
    const now = Math.floor(Date.now() / 1000);

    if (queueDocId) {
      await updateDoc(
        doc(
          firestore,
          `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/outgoingWhatsappMessagesQueue/${queueDocId}`,
        ),
        {
          status: "cancelled",
          updatedAt: now,
        },
      );
      return;
    }

    const q = query(
      collection(firestore, `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/outgoingWhatsappMessagesQueue`),
      where("chatId", "==", chatId),
      where("messageId", "==", messageId),
      where("status", "==", "pending"),
    );

    const snapshot = await getDocs(q);
    const promises = snapshot.docs.map((d) =>
      updateDoc(d.ref, {
        status: "cancelled",
        updatedAt: now,
      }),
    );
    await Promise.all(promises);
  };

  const createChatSession = async ({ contactName, recipientPhone }) => {
    const now = Math.floor(Date.now() / 1000);
    await setDoc(
      doc(
        firestore,
        `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_CHAT_SESSIONS_TABLE}/${recipientPhone}`,
      ),
      {
        contactName,
        recipientPhone,
        createdAt: now,
        updatedAt: now,
        messageCount: 0,
        unreadMessageCount: 0,
        mode: "manual",
        takenOverBy: null,
        takenOverAt: null,
        restoredBy: null,
        restoredAt: null,
      },
    );
    return recipientPhone;
  };

  const deleteChatSession = async (id) => {
    // First delete all messages in the subcollection
    const messagesRef = collection(
      firestore,
      `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_CHAT_SESSIONS_TABLE}/${id}/messages`,
    );
    const messagesSnapshot = await getDocs(messagesRef);
    const deletePromises = messagesSnapshot.docs.map((messageDoc) => deleteDoc(messageDoc.ref));
    await Promise.all(deletePromises);

    // Then delete the chat session document itself
    await deleteDoc(
      doc(firestore, `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_CHAT_SESSIONS_TABLE}/${id}`),
    );
  };

  return {
    data,
    error,
    loading: !data && !error,
    updateChatSession,
    saveOutgoingMessage,
    enqueueOutgoingWhatsappMessage,
    updateChatMessage,
    cancelOutgoingWhatsappMessage,
    createChatSession,
    deleteChatSession,
  };
}
